<template>
    <div>
        <div class="menu" :class="{'minimized': sidePanel.minimized, 'details': sidePanel.details}">
            <ul :class="{'hide-details': !sidePanel.details}">
                <template v-for="(item, index) in items">
                    <li class="sub-header" v-if="item.header" :key="'h' + index">{{ item.header }}</li>
                    <router-link v-else :key="'i' + index" :to="item.to" custom v-slot="{ navigate }">
                        <li @click="navigate" :class="{'active': item.active}">
                            <b-tooltip :label="item.label" position="is-right" append-to-body type="is-light" size="is-large">
                                <i class="fad fa-fw" :class="item.icon"></i>
                            </b-tooltip><span class="nav-title">{{ item.label }}</span>
                            <div class="meta" v-if="item.meta">{{ item.meta }}</div>
                            <div class="meta mt-3" v-if="item.flag">
                                <div><i class="fas fa-exclamation-triangle mr-2 is-size-6 activity-flag" :class="item.flag.cls"></i>{{ item.flag.text }}</div>
                            </div>
                        </li>
                    </router-link>
                </template>
            </ul>
        </div>
        <div class="controls">
            <div class="columns is-gapless">
                <div v-if="!sidePanel.minimized" class="column is-size-7 has-text-grey is-hidden-mobile" style="padding-top: 7px !important;">v{{ version }}</div>
                <div class="column has-text-right">
                    <span @click="toggleDetails()" v-if="!sidePanel.minimized"><i class="fad mt-2 mr-3" :class="{'fa-bars': sidePanel.details, 'fa-diagram-cells': !sidePanel.details}"></i></span>
                    <span @click="minimize(false)" v-if="sidePanel.minimized"><i class="fad fa-chevrons-right mt-2"></i></span>
                    <span @click="minimize(true)" v-else><i class="fad fa-chevrons-left mt-2"></i></span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import pkg from '../../../package.json';
    import {routeSection, NARROW_BREAKPOINT} from "../../constants/navigation";
    import navFlags from "./navFlags";

    const ATTENTION = {text: 'Requires Attention', cls: 'has-text-warning'};

    // Your Account sections (Account.vue reads ?section= from the route)
    const ACCOUNT_SECTIONS = [
        {key: 'account', label: 'Account Settings', icon: 'fa-user-gear', meta: 'Personal and contact information.'},
        {key: 'login', label: 'Login Credentials', icon: 'fa-lock-keyhole', meta: 'Email, phone and password used to sign in.'},
        {key: 'billing', label: 'Billing Information', icon: 'fa-credit-card', meta: 'Payment plans and payment methods.'},
        {key: 'schedule', label: 'Work Schedule', icon: 'fa-briefcase', meta: 'Your contracted work schedule.'},
        {key: 'pd', label: 'Professional Development', icon: 'fa-graduation-cap', meta: 'Renewal profile and clock-hour goals.'},
        {key: 'certs', label: 'Certifications & Organizations', icon: 'fa-certificate', meta: 'Certificate and organization IDs on file.'},
    ];

    export default {
        name: "SidePanelNav",
        mixins: [navFlags],
        data: function() {
            return {
                version: pkg.version
            };
        },
        computed: {
            ...mapGetters([
                'currentUser', 'sidePanel', 'hasExpiredCourses', 'expiredCourses', 'hasCartEnrollment',
                // dashboard shortcuts
                'activeEnrollmentCount'
            ]),
            section() {
                return routeSection(this.$route);
            },
            items() {
                const name = this.$route.name;
                const on = (...names) => names.includes(name);
                // One page's routed views (/enrollments, /enrollments/completed ...)
                // become sibling items. The default view links by path: vue-router
                // copies the current params on a same-name push, so `{name}` alone
                // would keep the current view.
                const view = (routeName, path, v, def) => ({
                    ...def,
                    to: v ? {name: routeName, params: {view: v}} : {path},
                    active: on(routeName) && (this.$route.params.view || '') === (v || '')
                });

                switch (this.section && this.section.key) {
                    case 'dashboard':
                        return this.dashboardItems();
                    case 'courses': {
                        const items = [
                            {header: 'Your Courses'},
                            view('enrollments', '/enrollments', null, {
                                label: 'Current Enrollments', icon: 'fa-circle-play',
                                meta: "Access, review and log time for the courses you're working on."
                            }),
                            view('enrollments', '/enrollments', 'completed', {
                                label: 'Completed Enrollments', icon: 'fa-check-to-slot',
                                meta: 'Finished courses, archived by certification and year.',
                                flag: this.incompleteCourseApps ? ATTENTION : null
                            }),
                        ];
                        if (this.hasExpiredCourses) {
                            items.push({
                                label: 'Reactivate Courses', icon: 'fa-redo', to: {name: 'reactivation-checkout'}, active: on('reactivation-checkout'),
                                meta: 'Restore access to expired course enrollments.',
                                flag: {text: this.expiredCourses.count + ' Expired', cls: 'has-text-warning'}
                            });
                        }
                        items.push(
                            {header: 'Enroll'},
                            {
                                label: 'Online Course Catalog', icon: 'fa-browsers', to: {name: 'course-catalog'}, active: on('course-catalog', 'course-enroll'),
                                meta: 'Browse online courses and offerings.'
                            },
                            {
                                label: 'Build a Bundle', icon: 'fa-layer-group', to: {name: 'package-builder'}, active: on('package-builder'),
                                meta: 'Choose several courses and enroll in them together.'
                            }
                        );
                        if (this.hasCartEnrollment) {
                            items.push({
                                label: 'Cart Checkout', icon: 'fa-shopping-cart', to: {name: 'cart-checkout'}, active: on('cart-checkout'),
                                meta: 'Finish enrolling in the courses waiting in your cart.',
                                flag: {text: 'Action Required', cls: 'has-text-warning'}
                            });
                        }
                        return items;
                    }
                    case 'conferences':
                        return [
                            {header: 'Your Conferences'},
                            {
                                label: 'Conference Records', icon: 'fa-screen-users', to: {name: 'registrations'}, active: on('registrations'),
                                meta: "Manage the in-person or virtual conferences or trainings you've attended.",
                                flag: this.incompleteConfApps ? ATTENTION : null
                            },
                            {header: 'Register'},
                            {
                                label: 'Conference Center', icon: 'fa-presentation', to: {name: 'conference-center'}, active: on('conference-center', 'register'),
                                meta: 'Register or get credit for a conference or remote content.'
                            },
                        ];
                    case 'records': {
                        const items = [{header: 'Certificates & Forms'}];
                        // Only worth a menu item while something is actually
                        // started (bare /reports lands on certificates otherwise)
                        if (this.inProgressApplicationCount > 0 || (on('reports') && this.$route.params.view === 'applications')) {
                            items.push(view('reports', '/reports', 'applications', {
                                label: 'In Progress Applications', icon: 'fa-tasks',
                                meta: "Applications you've started for certifications and completion forms."
                            }));
                        }
                        items.push(
                            view('reports', '/reports', 'completed', {
                                label: 'Completed Certificates', icon: 'fa-file-certificate',
                                meta: 'Approved certificates and official forms, ready to view or print.'
                            }),
                            view('reports', '/reports', 'receipts', {
                                label: 'Receipts & Invoices', icon: 'fa-file-invoice',
                                meta: 'Billing documents published to your account.'
                            }),
                            {header: 'Reports'},
                            {
                                label: 'Study Activity', icon: 'fa-calendar-days', to: {name: 'study-activity'}, active: on('study-activity'),
                                meta: 'See all of your logged time across courses and conferences in one calendar.',
                                flag: this.activityFlag
                            },
                        );
                        return items;
                    }
                    case 'account': {
                        const current = this.$route.params.section || 'account';
                        return [
                            {header: 'Settings'},
                            ...ACCOUNT_SECTIONS.map(s => ({
                                label: s.label, icon: s.icon, meta: s.meta,
                                // default section lives at the bare /account path
                                to: s.key === 'account' ? {path: '/account'} : {name: 'account', params: {section: s.key}},
                                active: on('account') && current === s.key
                            }))
                        ];
                    }
                    case 'support':
                        return [
                            {header: 'Support'},
                            view('support', '/support', null, {
                                label: 'Your Tickets', icon: 'fa-ticket',
                                meta: 'Open and past support conversations.'
                            }),
                            view('support', '/support', 'new', {
                                label: 'New Ticket', icon: 'fa-plus-circle',
                                meta: 'Ask a question or report a problem.'
                            }),
                        ];
                    default:
                        return [];
                }
            }
        },
        methods: {
            // The dashboard's panel is shortcuts only (no to-dos, no course
            // list): links into the sections, with the personal ones surfacing
            // only when the student has something there.
            dashboardItems() {
                const items = [{header: 'Shortcuts'}];
                const active = this.activeEnrollmentCount || 0;
                if (active > 0) {
                    items.push({
                        label: 'Current Enrollments', icon: 'fa-circle-play', to: {path: '/enrollments'},
                        meta: active === 1 ? 'Pick up the course you have in progress.' : 'Pick up any of the ' + active + ' courses you have in progress.'
                    });
                }
                if ((this.registrations || []).length > 0) {
                    items.push({
                        label: 'Conference Records', icon: 'fa-screen-users', to: {name: 'registrations'},
                        meta: 'Your conference registrations and attendance records.',
                        flag: this.incompleteConfApps ? ATTENTION : null
                    });
                }
                items.push(
                    {label: 'Browse the Course Catalog', icon: 'fa-browsers', meta: 'Find and enroll in online courses.', to: {name: 'course-catalog'}},
                    {label: 'Register for a Conference', icon: 'fa-presentation', meta: 'Conference Center registrations and credit.', to: {name: 'conference-center'}},
                    {label: 'Get Help', icon: 'fa-life-ring', meta: 'Open or follow up on a support ticket.', to: {name: 'support'}}
                );
                return items;
            },
            toggleDetails: function() {
                this.$store.commit('setDetails', !this.sidePanel.details);
            },
            minimize: function(state) {
                this.$store.commit('setMinimized', state);
            },
            autoSize: function() {
                let width = window.innerWidth;
                if (width < NARROW_BREAKPOINT) {
                    this.$store.commit('setMinimized', true);
                } else {
                    this.$store.commit('setMinimized', false);
                }
            }
        },
        mounted() {
            window.addEventListener('resize', this.autoSize);
            this.autoSize();
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.autoSize);
        }
    };
</script>

<style lang="scss" scoped>
    @import "~@/assets/scss/sidemenu";
</style>
